import React from "react";
import {
  Alert,
  AlertTitle,
  Box,
  Button,
  LinearProgress,
  Typography,
} from "@mui/material";
import {
  AccessTime as AccessTimeIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import { withTranslation } from "react-i18next";

function TrialBanner({ t, trialDaysRemaining, trialEnd, onUpgradeClick, compact = false }) {
  const isUrgent = trialDaysRemaining <= 3;
  const isExpired = trialDaysRemaining <= 0;

  // Calculate progress (14 day trial)
  const totalDays = 14;
  const daysUsed = totalDays - trialDaysRemaining;
  const progress = Math.min((daysUsed / totalDays) * 100, 100);

  if (isExpired) {
    return (
      <Alert
        severity="error"
        icon={<WarningIcon />}
        action={
          <Button
            color="inherit"
            size="small"
            variant="outlined"
            onClick={onUpgradeClick}
          >
            {t("Upgrade Now")}
          </Button>
        }
        sx={{ mb: compact ? 0 : 3 }}
      >
        <AlertTitle>{t("Trial Expired")}</AlertTitle>
        {t("Your trial has expired. Upgrade to continue using all features.")}
      </Alert>
    );
  }

  if (compact) {
    return (
      <Alert
        severity={isUrgent ? "warning" : "info"}
        icon={<AccessTimeIcon />}
        action={
          <Button
            color="inherit"
            size="small"
            variant="outlined"
            onClick={onUpgradeClick}
          >
            {t("Upgrade")}
          </Button>
        }
      >
        {trialDaysRemaining === 1
          ? t("1 day left in trial")
          : t("{{count}} days left in trial", { count: trialDaysRemaining })}
      </Alert>
    );
  }

  return (
    <Alert
      severity={isUrgent ? "warning" : "info"}
      icon={<AccessTimeIcon />}
      sx={{ mb: 3 }}
    >
      <AlertTitle>
        {isUrgent ? t("Trial Ending Soon") : t("Free Trial")}
      </AlertTitle>
      <Box sx={{ mt: 1 }}>
        <Box sx={{ display: "flex", justifyContent: "space-between", mb: 1 }}>
          <Typography variant="body2">
            {trialDaysRemaining === 1
              ? t("1 day remaining")
              : t("{{count}} days remaining", { count: trialDaysRemaining })}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {t("{{used}} of {{total}} days used", {
              used: daysUsed,
              total: totalDays,
            })}
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={progress}
          color={isUrgent ? "warning" : "info"}
          sx={{ mb: 2, height: 8, borderRadius: 1 }}
        />
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 2 }}>
          <Typography variant="body2" color="text.secondary">
            {t("Upgrade anytime to keep all your data and unlock full features.")}
          </Typography>
          <Button
            variant={isUrgent ? "contained" : "outlined"}
            color={isUrgent ? "warning" : "primary"}
            size="small"
            onClick={() => {
              const plansSection = document.getElementById("plans-section");
              if (plansSection) {
                plansSection.scrollIntoView({ behavior: "smooth" });
              }
              if (onUpgradeClick) onUpgradeClick();
            }}
          >
            {t("Upgrade Now")}
          </Button>
        </Box>
      </Box>
    </Alert>
  );
}

export default withTranslation()(TrialBanner);
